import React from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Award, Calendar, BookOpen, Video, Image as ImageIcon, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const AboutMilestones = () => (
  <div className="space-y-8">
    <div className="text-center mb-12">
      <h2 className="text-4xl font-bold text-gray-900 mb-4">Milestones & Historic Events</h2>
      <p className="text-gray-600 max-w-3xl mx-auto">
        Celebrating landmark achievements in business, spiritual revival, and global impact
      </p>
    </div>

    {/* Verified Retreat Photos from Sunyatimes */}
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 mb-12">
      <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Sunya Retreats & Activities</h3>
      <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
        Authentic images from official Sunyatee International Foundation retreats and programs
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {mockData.sunyateeGallery?.events?.map((event) => (
          <div key={event.id} className="group cursor-pointer">
            <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
              <img
                src={event.image}
                alt={event.title}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h4 className="text-sm font-semibold text-gray-900 mt-3">{event.title}</h4>
            <p className="text-xs text-gray-600 mt-1">{event.description}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Major Milestones Timeline */}
    <div className="space-y-6">
      {mockData.milestoneEvents.map((event, index) => (
        <div
          key={event.id}
          className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow`}
        >
          {/* Image */}
          <div className="md:w-1/2 h-64 md:h-auto">
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="md:w-1/2 p-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Calendar className="text-emerald-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-emerald-700 font-semibold">{event.date}</p>
                <p className="text-xs text-gray-500">{event.location}</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-3">{event.title}</h3>
            <p className="text-gray-600 mb-4">{event.description}</p>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">Key Highlights:</p>
              <ul className="space-y-1">
                {event.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-700">
                    <span className="text-emerald-600 mr-2">✓</span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Special Recognition Section */}
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">World Records & Recognition</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-amber-500 text-white flex items-center justify-center mx-auto mb-4 text-3xl font-bold">
            🏆
          </div>
          <h4 className="font-bold text-gray-900 mb-2">Mass Meditation Gatherings</h4>
          <p className="text-sm text-gray-600">Thousands of practitioners gathering at Bodhgaya for Sunya meditation practice</p>
        </div>
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto mb-4 text-3xl font-bold">
            📜
          </div>
          <h4 className="font-bold text-gray-900 mb-2">World's First Palm Leaf Printing Unit</h4>
          <p className="text-sm text-gray-600">Pioneering the revival of ancient sutra printing on 1000-year durable palm leaves</p>
        </div>
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-blue-500 text-white flex items-center justify-center mx-auto mb-4 text-3xl font-bold">
            🌏
          </div>
          <h4 className="font-bold text-gray-900 mb-2">Global DXN Network</h4>
          <p className="text-sm text-gray-600">180+ countries with 15-20 million distributors - one of the largest wellness networks</p>
        </div>
      </div>
    </div>

    {/* Palm Leaf Project Details */}
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Palm Leaf Sutra Printing Project</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Project Vision</h4>
          <p className="text-gray-700 mb-4">
            Reviving ancient Indian wisdom by printing sutras on specially prepared palm leaves using advanced laser technology. Each palm leaf manuscript lasts over 1,000 years, is waterproof, and resists environmental damage.
          </p>
          <h4 className="font-semibold text-gray-900 mb-3">Key Achievements:</h4>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Launched at 5th Sunya Mega Retreat, Bodhgaya</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Vajra Chedika Prajna Paramita Sutra published</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Support for traditional palm leaf artists</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Collaboration with Nalanda & Takshashila universities</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Translation Initiative:</h4>
          <p className="text-gray-700 mb-4">
            Led by Hyderabad-based software engineer Prateek Vooshmalla and team, sutras are being translated into:
          </p>
          <div className="grid grid-cols-2 gap-2">
            {['English', 'Hindi', 'Malayalam', 'Tamil', 'Telugu', 'Marathi'].map((lang, idx) => (
              <div key={idx} className="bg-white rounded-lg px-3 py-2 text-center text-sm font-semibold text-gray-800 shadow-sm">
                {lang}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Access all translations at: <a href="https://TheIndianSutras.com" className="text-purple-700 hover:text-purple-800 font-semibold">TheIndianSutras.com</a>
          </p>
        </div>
      </div>
    </div>
  </div>
);

const AboutBiography = () => (
  <div className="space-y-8">
    <div className="prose max-w-none">
      <div className="bg-gradient-to-br from-emerald-50 to-gray-50 rounded-2xl p-8 mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <img
              src="https://www.dxn2u.com/founder/images/Datuk-Lim.jpg"
              alt="Dr. Lim Siow Jin"
              className="w-full h-auto rounded-xl shadow-lg"
            />
          </div>
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-3xl font-bold text-gray-900">The Journey: From Engineer to Zen Master</h2>
            <p className="text-gray-700 leading-relaxed">
              Born in 1953 in Alor Setar, Kedah, Malaysia, Datuk Dr. Lim Siow Jin's journey is one of remarkable transformation and integration. From his education at the prestigious Indian Institute of Technology (IIT Kharagpur) as a civil engineer to becoming a billionaire entrepreneur, Zen master, and wellness pioneer, his life embodies the principle that business, health, and spirituality can coexist harmoniously.
            </p>
            <p className="text-gray-700 leading-relaxed">
              After completing his engineering degree, Dr. Lim embarked on a path that would eventually lead him to discover the profound healing properties of Ganoderma Lucidum (Reishi mushroom). This discovery became the foundation of DXN Holdings Bhd, which he founded in 1993 with a vision to make Ganoderma a household wellness product worldwide.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
            <BookOpen className="text-emerald-600 mr-2" size={24} />
            Education & Academic Honors
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>Indian Institute of Technology (IIT Kharagpur) - Civil Engineering</span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>Doctorate in Science (Alternative Medicines)</span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>Honorary Professor - Academie Scientifique Internationale, France</span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>Senior Visiting Professor - Open International University for Alternative Medicines</span>
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
            <Award className="text-amber-600 mr-2" size={24} />
            Key Milestones
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>1953 - Born in Alor Setar, Kedah</span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>1993 - Founded DXN Holdings Bhd</span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>2003 - DXN Listed on Bursa Malaysia</span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>2015 - Founded Sunyatee International Foundation</span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2">•</span>
              <span>2023 - DXN 30th Anniversary Celebration</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">The Philosophy: One Life, Three Pillars</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Dr. Lim's unique philosophy integrates three essential dimensions of human existence:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-bold text-2xl">1</span>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Business for Prosperity</h4>
            <p className="text-sm text-gray-600">Creating economic opportunities and empowering millions through ethical network marketing</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-bold text-2xl">2</span>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Wellness for Health</h4>
            <p className="text-sm text-gray-600">Combining 2000+ years of TCM wisdom with modern Ganoderma research</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-bold text-2xl">3</span>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">Spirituality for Peace</h4>
            <p className="text-sm text-gray-600">Universal Sunya meditation accessible to all, regardless of background</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AboutAwards = () => (
  <div className="space-y-8">
    <div className="text-center mb-12">
      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
          <Award className="text-white" size={40} />
        </div>
      </div>
      <h2 className="text-4xl font-bold text-gray-900 mb-4">Awards & Recognition</h2>
      <p className="text-gray-600 max-w-2xl mx-auto">
        Honored globally for outstanding contributions to business, wellness, humanitarian service, and peace advocacy
      </p>
    </div>

    <div className="space-y-8">
      {/* Malaysian National Honors */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-2 h-8 bg-red-600 mr-3"></div>
          Malaysian National Honors
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mockData.awards.filter(a => a.type === 'National Honor' || a.type === 'State Honor').map((award, index) => (
            <div key={index} className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border-2 border-red-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-3">
                <Award className="text-red-600 flex-shrink-0 mt-1" size={24} />
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-1">{award.title}</h4>
                  <p className="text-sm text-gray-600 mb-1">{award.issuer}</p>
                  {award.description && (
                    <p className="text-xs text-gray-500 mb-2">{award.description}</p>
                  )}
                  <p className="text-xs text-red-700 font-semibold">{award.year}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* International Awards */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-2 h-8 bg-blue-600 mr-3"></div>
          International Awards & Recognition
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mockData.awards.filter(a => a.type === 'International').map((award, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-blue-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-3">
                <Award className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-1 text-sm">{award.title}</h4>
                  <p className="text-xs text-gray-600 mb-1">{award.issuer}</p>
                  {award.description && (
                    <p className="text-xs text-gray-500 mb-2">{award.description}</p>
                  )}
                  <p className="text-xs text-blue-700 font-semibold">{award.year}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Academic & Professional */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-2 h-8 bg-emerald-600 mr-3"></div>
          Academic & Professional Honors
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mockData.awards.filter(a => a.type === 'Academic' || a.type === 'Professional' || a.type === 'Fellowship').map((award, index) => (
            <div key={index} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-3">
                <Award className="text-emerald-600 flex-shrink-0 mt-1" size={24} />
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-1">{award.title}</h4>
                  <p className="text-sm text-gray-600 mb-1">{award.issuer}</p>
                  {award.description && (
                    <p className="text-xs text-gray-500 mb-2">{award.description}</p>
                  )}
                  <p className="text-xs text-emerald-700 font-semibold">{award.year}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Business Excellence */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-2 h-8 bg-amber-600 mr-3"></div>
          Business Excellence
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mockData.awards.filter(a => a.type === 'Business' || a.type === 'Recognition').map((award, index) => (
            <div key={index} className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-3">
                <Award className="text-amber-600 flex-shrink-0 mt-1" size={24} />
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-1">{award.title}</h4>
                  <p className="text-sm text-gray-600 mb-1">{award.issuer}</p>
                  {award.description && (
                    <p className="text-xs text-gray-500 mb-2">{award.description}</p>
                  )}
                  <p className="text-xs text-amber-700 font-semibold">{award.year}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Timeline Section */}
    <div className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-2xl p-8 mt-12">
      <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Awards Timeline</h3>
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-emerald-600 to-amber-600"></div>
          {['2001', '2002', '2004', '2005', '2007', '2010', '2012', '2019', '2023'].map((year, idx) => (
            <div key={idx} className="relative mb-8 flex items-center">
              <div className="w-1/2 pr-8 text-right">
                {idx % 2 === 0 && (
                  <div className="bg-white rounded-lg p-4 shadow-md">
                    <p className="font-bold text-emerald-700">{year}</p>
                    <p className="text-sm text-gray-600">Multiple honors received</p>
                  </div>
                )}
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-amber-500 border-4 border-white shadow-lg"></div>
              <div className="w-1/2 pl-8">
                {idx % 2 !== 0 && (
                  <div className="bg-white rounded-lg p-4 shadow-md">
                    <p className="font-bold text-emerald-700">{year}</p>
                    <p className="text-sm text-gray-600">Multiple honors received</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const About = () => {
  const { section } = useParams();
  const location = useLocation();

  const sections = [
    { id: 'biography', name: 'Biography & Journey', component: AboutBiography },
    { id: 'awards', name: 'Awards & Recognition', component: AboutAwards },
    { id: 'milestones', name: 'Milestones & Events', component: AboutMilestones },
    { id: 'philosophy', name: 'Philosophy & Vision', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Philosophy & Vision - Coming Soon</h2></div> },
    { id: 'media', name: 'In the Media', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Media Coverage - Coming Soon</h2></div> },
    { id: 'gallery', name: 'Photo Gallery', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Photo Gallery - Coming Soon</h2></div> }
  ];

  const currentSection = section || 'biography';
  const CurrentComponent = sections.find(s => s.id === currentSection)?.component || AboutBiography;

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="About Dr. Lim Siow Jin"
        description="Learn about Datuk Dr. Lim Siow Jin (Dhyanacharya Nagajiva) - Founder of DXN Holdings, Zen Master, and Wellness Pioneer. From IIT Kharagpur to building a billion-dollar wellness empire."
        keywords="Dr. Lim Siow Jin biography, Dhyanacharya Nagajiva, DXN founder story, Sunya meditation teacher, wellness pioneer, Malaysian entrepreneur"
        
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">About Dr. Lim Siow Jin</h1>
              <p className="text-xl text-emerald-100">Dhyanacharya Nagajiva</p>
            </div>
            <SocialShare 
              title="About Dr. Lim Siow Jin - Biography, Awards & Milestones"
              description="Learn about the visionary entrepreneur, Zen master, and wellness pioneer behind DXN and Sunya meditation."
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {sections.map((sec) => (
              <Link
                key={sec.id}
                to={`/about/${sec.id}`}
                className={`px-6 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  currentSection === sec.id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-600 hover:text-emerald-700 hover:border-gray-300'
                }`}
              >
                {sec.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <CurrentComponent />
      </div>
    </div>
  );
};

export default About;